import type * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import { Workbook } from '../../models'

/**
 * Represents the actual contents of a download
 */
export interface Download {
  readonly filename: string
  readonly workbook: Workbook
}
export namespace Download {
  export const isDownload: TypeChecker<Download> =
    TypeChecker.Factory.object<Download>({
      filename: TypeChecker.isString,
      workbook: TypeChecker.Factory.instance(Workbook),
    })

  /**
   * Produces a {@link Download} for the given list of entities
   */
  export type Producer<TTypeParams extends ec.models.TypeParams.Constraint> = (
    params: Producer.Params<TTypeParams>,
  ) => MaybeObservable<Download>
  export namespace Producer {
    export interface Params<
      TTypeParams extends ec.models.TypeParams.Constraint,
    > {
      readonly attributes: readonly ec.models.Attribute<TTypeParams>[]
      readonly entities: readonly TTypeParams['entity'][]
    }

    /**
     * Allows consumers to return a partial {@link Download}, which is completed
     * using default values
     */
    export type Patcher<TTypeParams extends ec.models.TypeParams.Constraint> = (
      params: Producer.Params<TTypeParams>,
    ) => MaybeObservable<Partial<Download>>

    /**
     * Creates a {@link Download.Producer} by combining a base
     * {@link Download.Producer} with a {@link Download.Producer.Patcher}
     */
    export function from<TTypeParams extends ec.models.TypeParams.Constraint>(
      baseProducer: Producer<TTypeParams>,
      patcher?: Patcher<TTypeParams>,
    ): Producer<TTypeParams> {
      if (!patcher) {
        // If there is no patcher, we'll just use the base producer
        return baseProducer
      }
      return (params: Producer.Params<TTypeParams>) => {
        const patchRaw = patcher(params)
        const patch$ = rxjs.isObservable(patchRaw)
          ? patchRaw
          : rxjs.of(patchRaw)
        // NOTE: we lazily call baseProducer because if the patch contains all
        // of the properties, we don't want to bother creating the default Excel
        // workbook
        let _base: Download | rxjs.Observable<Download> | undefined
        const getBase = () => (_base ??= baseProducer(params))
        return patch$.pipe(
          rxjs.switchMap(patch => {
            if (isDownload(patch)) {
              // If the patch is a complete download, we can just return it
              return rxjs.of(patch)
            }
            // Otherwise, we need to create a download using the base producer
            const baseRaw = getBase()
            const base$ = rxjs.isObservable(baseRaw)
              ? baseRaw
              : rxjs.of(baseRaw)
            return base$.pipe(
              rxjs.map(
                base =>
                  // NOTE: we write every property since there aren't that many,
                  // and it ensures we handle when a property is not omitted but
                  // is still undefined (spread operators do not handle that case
                  // well)
                  ({
                    filename: patch.filename ?? base.filename,
                    workbook: patch.workbook ?? base.workbook,
                  }) satisfies NoMissing<Download>,
              ),
            )
          }),
        )
      }
    }
  }
}

type MaybeObservable<T> = T | rxjs.Observable<T>
